import { Box, IconButton, List, ListItem, Popover, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { InfoIcon } from "@phosphor-icons/react/dist/csr/Info";
import convert from "color-convert";
import cytoscape, { type Core, type ElementDefinition } from "cytoscape";
import dagre from "cytoscape-dagre";
import { useEffect, useMemo, useRef, useState, type FC } from "react";
import { LEGEND_ORDER, NODE_TYPES, getNodeTypeKey, isBqQueryStepKind, type NodeTypeKey } from "../nodeTypes";
import type { DataFlow, DataFlowStep } from "../types";

let dagreRegistered = false;

let cachedCanvas: HTMLCanvasElement | undefined;

function getTextWidth(text: string, font: string): number {
  const canvas = cachedCanvas ?? (cachedCanvas = document.createElement("canvas"));
  const context = canvas.getContext("2d");
  if (!context) return 100;
  context.font = font;
  return context.measureText(text).width;
}

function adjustColor(primaryColor: string, percent: number): string {
  if (percent > -0.001 && percent < 0.001) return primaryColor;
  const [h, s, l] = convert.hex.hsl(primaryColor);
  const newL = Math.max(0, Math.min(100, l + percent));
  return `#${convert.hsl.hex([h, s, newL])}`;
}

function splitTableLabel(name: string): string {
  const dotIndex = name.indexOf(".");
  if (dotIndex === -1) return name;
  return name.slice(0, dotIndex + 1) + "\n" + name.slice(dotIndex + 1);
}

function shadowEffect(color: string, blur: number, opacity: number): Record<string, unknown> {
  return {
    "shadow-blur": blur,
    "shadow-color": color,
    "shadow-opacity": opacity,
    "shadow-offset-x": 0,
    "shadow-offset-y": 0,
  };
}

function buildElements(dataFlow: DataFlow, labelFont: string): ElementDefinition[] {
  const elements: ElementDefinition[] = [];

  for (const step of dataFlow.steps) {
    const isTable = step.kind.toLowerCase() === "table";
    const isBundleTable = step.kind === "BundleTable";
    let kindClass = "node-default";
    switch (step.kind.toLowerCase()) {
      case "source":
        kindClass = "node-source";
        break;
      case "transformation":
        kindClass = "node-transform";
        break;
      case "sink":
        kindClass = "node-sink";
        break;
      case "table":
        kindClass = step.isDestTable === true ? "node-table-dest" : "node-table";
        break;
      case "bundletable":
        kindClass = "node-bundle-table";
        break;
    }
    if (step.hasDestTables) {
      kindClass = "node-processing";
    } else if (isBqQueryStepKind(step.stepKind)) {
      kindClass = "node-bq-query";
    }

    let innerLabel = "";
    switch (step.kind.toLowerCase()) {
      case "source":
        innerLabel = step.hasDestTables ? "Processing" : "Download";
        break;
      case "sink":
        innerLabel = "Upload";
        break;
      case "table":
      case "bundletable":
        innerLabel = "Table";
        break;
    }

    // Bundle tables are named "download <bundle>.<table>"; show just the table portion — the
    // parent bundle download node sits directly above and already carries the bundle name.
    const bundleTableLabel = step.declarationName.slice(step.declarationName.indexOf(".") + 1);
    const nameLabel = isBundleTable
      ? splitTableLabel(bundleTableLabel)
      : isTable
        ? splitTableLabel(step.name)
        : step.name;
    const labelWidth = innerLabel ? getTextWidth(innerLabel, labelFont) * 1.4 + 16 : 60;

    const groupId = `${step.name}__label`;

    // Transparent compound parent — shows step name below the shape
    elements.push({
      data: { id: groupId, label: nameLabel },
      classes: "node-group",
    });

    // Visible child shape — shows short type text inside
    elements.push({
      data: {
        id: step.name,
        parent: groupId,
        label: innerLabel,
        labelWidth,
        kind: step.kind,
        stepKind: step.stepKind,
        project: step.project,
        declarationName: step.declarationName,
        line: step.line,
        isDestTable: step.isDestTable,
        hasDestTables: step.hasDestTables,
      },
      classes: kindClass,
    });
  }

  for (const sourceStep of dataFlow.steps) {
    for (const targetName of sourceStep.outputs) {
      if (dataFlow.steps.some((s) => s.name === targetName)) {
        elements.push({
          data: {
            id: `${sourceStep.name}_to_${targetName}`,
            source: sourceStep.name,
            target: targetName,
          },
          classes: "edge-flow",
        });
      }
    }
  }

  return elements;
}

interface DataFlowViewerProps {
  dataFlow: DataFlow;
  onNodeSelect: (node: DataFlowStep | null) => void;
  highlightedNodeName?: string;
  selectedNodeName?: string;
}

export const DataFlowViewer: FC<DataFlowViewerProps> = ({
  dataFlow,
  onNodeSelect,
  highlightedNodeName,
  selectedNodeName,
}) => {
  const cyRef = useRef<HTMLDivElement>(null);
  const cyInstanceRef = useRef<Core | null>(null);
  const theme = useTheme();
  const labelTypography = useMemo(() => ({ ...theme.typography["body.small.regular"] }), [theme]);
  const labelFont = `normal ${String(labelTypography.fontWeight)} ${String(labelTypography.fontSize)} ${String(labelTypography.fontFamily)}`;

  const elements = useMemo(() => buildElements(dataFlow, labelFont), [dataFlow, labelFont]);
  const [helpAnchor, setHelpAnchor] = useState<HTMLElement | null>(null);

  const backgroundColorAdjust = theme.palette.mode === "light" ? -30 : -10;
  const borderColorAdjust = theme.palette.mode === "light" ? -40 : 10;
  const sourceBackgroundColorAdjust = theme.palette.mode === "light" ? -10 : -5;
  useEffect(() => {
    if (!dagreRegistered) {
      cytoscape.use(dagre);
      dagreRegistered = true;
    }

    const cy = cytoscape({
      container: cyRef.current,
      elements,
      minZoom: 0.2,
      maxZoom: 3,
      style: [
        {
          selector: "node",
          style: {
            label: "data(label)",
            shape: "round-rectangle",
            width: "data(labelWidth)",
            height: "32px",
            "background-color": theme.palette.surfaceDark10.main,
            "border-color": theme.palette.surfaceDark30.main as string,
            "border-width": 2,
            "text-valign": "center",
            color: theme.palette.text10.main,
            "text-wrap": "wrap",
            padding: "8px",
            "font-family": String(labelTypography.fontFamily),
            "font-size": String(labelTypography.fontSize),
            "font-weight": labelTypography.fontWeight as number,
          },
        },
        {
          selector: "node.node-group",
          style: {
            "background-opacity": 0,
            "border-width": 0,
            "text-valign": "bottom",
            "text-halign": "center",
            "text-margin-y": 6,
            "text-wrap": "wrap",
            padding: "0px",
            color: theme.palette.text30.main,
            "text-background-color": theme.palette.surfaceLight10.main,
            "text-background-opacity": 1,
            "text-background-padding": "4px",
            "text-background-shape": "roundrectangle",
            "z-index": 10,
            "z-index-compare": "manual",
            "z-compound-depth": "top",
          },
        },
        {
          selector: "node.node-source",
          style: {
            "background-color": adjustColor(theme.palette.dataPeach.main, sourceBackgroundColorAdjust),
            "border-color": adjustColor(theme.palette.dataPeach.main, borderColorAdjust),
            color: NODE_TYPES.download.textColor,
          },
        },
        {
          selector: "node.node-transform",
          style: {
            "background-color": adjustColor(theme.palette.dataSky.main, backgroundColorAdjust),
            "border-color": adjustColor(theme.palette.dataSky.main, borderColorAdjust),
            color: NODE_TYPES.transformation.textColor,
          },
        },
        {
          selector: "node.node-sink",
          style: {
            "background-color": adjustColor(theme.palette.dataCream.main, backgroundColorAdjust),
            "border-color": adjustColor(theme.palette.dataCream.main, borderColorAdjust),
            color: NODE_TYPES.upload.textColor,
          },
        },
        {
          selector: "node.node-bq-query",
          style: {
            "background-color": adjustColor(theme.palette.dataGinger.main, backgroundColorAdjust),
            "border-color": adjustColor(theme.palette.dataGinger.main, borderColorAdjust),
            color: NODE_TYPES.bqQuery.textColor,
          },
        },
        {
          selector: "node.node-processing",
          style: {
            "background-color": adjustColor(theme.palette.dataMaroon.main, backgroundColorAdjust),
            "border-color": adjustColor(theme.palette.dataMaroon.main, borderColorAdjust),
            color: NODE_TYPES.processing.textColor,
          },
        },
        {
          selector: "node.node-table",
          style: {
            shape: "ellipse",
            height: "48px",
            padding: "16px",
            "background-color": adjustColor(theme.palette.dataArctic.main, backgroundColorAdjust),
            "border-color": adjustColor(theme.palette.dataArctic.main, borderColorAdjust),
            color: NODE_TYPES.table.textColor,
          },
        },
        {
          selector: "node.node-table-dest",
          style: {
            shape: "ellipse",
            height: "48px",
            padding: "16px",
            "background-color": adjustColor(theme.palette.dataCerulean.main, backgroundColorAdjust),
            "border-color": adjustColor(theme.palette.dataCerulean.main, borderColorAdjust),
            color: NODE_TYPES.destinationTable.textColor,
          },
        },
        {
          selector: "node.node-bundle-table",
          style: {
            shape: "ellipse",
            height: "48px",
            padding: "16px",
            "background-color": adjustColor(theme.palette.dataLime.main, backgroundColorAdjust),
            "border-color": adjustColor(theme.palette.dataLime.main, borderColorAdjust),
            color: NODE_TYPES.bundleTable.textColor,
          },
        },
        {
          selector: "node.node-selected",
          style: {
            "border-width": 4,
            "border-color": theme.palette.accentInfo30.main,
            ...shadowEffect(theme.palette.accentInfo30.main, 8, 0.4),
          },
        },
        {
          selector: "node.node-highlighted",
          style: {
            "border-width": 5,
            "border-color": theme.palette.primary.main,
            ...shadowEffect(theme.palette.primary.main, 10, 0.45),
          },
        },
        {
          selector: "edge",
          style: {
            width: 2,
            "line-color": theme.palette.text30.main,
            "target-arrow-color": theme.palette.text30.main,
            "target-arrow-shape": "triangle",
            "curve-style": "bezier",
            "z-index": 0,
            "z-index-compare": "manual",
          },
        },
      ],
    });

    const LINE_HEIGHT = 20;
    const STAGGER_MULTIPLIER = 4.0;
    const maxGroupLabelLines = elements
      .filter((el) => el.classes === "node-group")
      .reduce(
        (max, el) => Math.max(max, String((el.data as Record<string, unknown>).label ?? "").split("\n").length),
        1
      );
    const rankSep = (maxGroupLabelLines * LINE_HEIGHT * STAGGER_MULTIPLIER + 60) / 1.3;

    const layout = cy.layout({ name: "dagre", rankSep } as Parameters<typeof cy.layout>[0]);
    layout.on("layoutstop", () => {
      // Group compound nodes by rank (same Y after dagre), then stagger alternate
      // nodes down by enough to clear their multi-line labels.

      const RANK_TOLERANCE = 20;
      const rankMap = new Map<number, cytoscape.NodeSingular[]>();
      (cy.nodes(".node-group") as cytoscape.NodeCollection).forEach((node) => {
        const rankY = Math.round(node.position("y") / RANK_TOLERANCE) * RANK_TOLERANCE;
        let bucket = rankMap.get(rankY);
        if (!bucket) {
          bucket = [];
          rankMap.set(rankY, bucket);
        }
        bucket.push(node as cytoscape.NodeSingular);
      });

      rankMap.forEach((nodes) => {
        if (nodes.length < 2) return;
        const maxLines = Math.max(...nodes.map((n) => String(n.data("label")).split("\n").length));
        const stagger = maxLines * LINE_HEIGHT * STAGGER_MULTIPLIER;
        nodes
          .sort((a, b) => a.position("x") - b.position("x"))
          .forEach((node, i) => {
            const yShift = i === 0 ? 0 : [stagger, -stagger, 0][(i - 1) % 3];
            if (yShift !== 0) node.shift({ x: 0, y: yShift });
          });
      });
      cy.fit(undefined, 20);
    });
    layout.run();

    if (highlightedNodeName) {
      cy.getElementById(highlightedNodeName).addClass("node-highlighted");
    }

    cy.on("tap", "node", (event) => {
      let tapped = event.target as cytoscape.NodeSingular;
      if (tapped.hasClass("node-group")) {
        const child = tapped
          .children()
          .filter((n) => !n.hasClass("node-group"))
          .first();
        if (child.empty()) return;
        tapped = child as cytoscape.NodeSingular;
      }
      const d = tapped.data() as {
        id: string;
        kind: string;
        stepKind: string | undefined;
        project: string;
        declarationName: string;
        line: number;
        isDestTable: boolean | undefined;
        hasDestTables: boolean | undefined;
      };
      onNodeSelect({
        kind: d.kind,
        name: d.id,
        declarationName: d.declarationName,
        project: d.project,
        line: d.line,
        inputs: [],
        outputs: [],
        stepKind: d.stepKind,
        isDestTable: d.isDestTable,
        hasDestTables: d.hasDestTables,
      });
    });

    cy.on("tap", (event) => {
      if (event.target === cy) {
        onNodeSelect(null);
      }
    });

    cyInstanceRef.current = cy;
    return () => {
      cyInstanceRef.current = null;
      cy.destroy();
    };
  }, [
    elements,
    theme,
    backgroundColorAdjust,
    borderColorAdjust,
    sourceBackgroundColorAdjust,
    labelTypography,
    onNodeSelect,
    highlightedNodeName,
  ]);

  useEffect(() => {
    const cy = cyInstanceRef.current;
    if (!cy) return;
    cy.nodes().removeClass("node-selected");
    if (selectedNodeName) {
      cy.getElementById(selectedNodeName).addClass("node-selected");
    }
  }, [selectedNodeName]);

  const presentTypeKeys = useMemo(() => {
    const set = new Set<NodeTypeKey>();
    for (const step of dataFlow.steps) {
      const key = getNodeTypeKey(step);
      if (key) set.add(key);
    }
    return set;
  }, [dataFlow]);

  const legendItems = LEGEND_ORDER.filter((key) => presentTypeKeys.has(key)).map((key) => {
    const meta = NODE_TYPES[key];
    return {
      label: meta.label,
      color: theme.palette[meta.paletteToken].main,
      shape: meta.shape,
      bgAdjust: key === "download" ? sourceBackgroundColorAdjust : undefined,
    };
  });

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      <Box ref={cyRef} sx={{ width: "100%", height: "100%" }} />
      {legendItems.length > 0 && (
        <Box
          sx={(t) => ({
            position: "absolute",
            bottom: 8,
            left: 8,
            bgcolor: "surfaceLight10.main",
            border: `1px solid ${t.palette.surfaceLight30.main}`,
            borderRadius: "8px",
            boxShadow: t.mixins.shadowSurfaceLight10,
            px: 1.25,
            py: 1,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            ...t.applyStyles("dark", {
              boxShadow: t.mixins.shadowSurfaceDark10,
            }),
          })}
        >
          {legendItems.map((item) => (
            <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  bgcolor: adjustColor(item.color, item.bgAdjust ?? backgroundColorAdjust),
                  border: "2px solid",
                  borderColor: adjustColor(item.color, borderColorAdjust),
                  borderRadius: item.shape === "ellipse" ? "50%" : "3px",
                  flexShrink: 0,
                }}
              />
              <Typography variant="body.small.regular">{item.label}</Typography>
            </Box>
          ))}
        </Box>
      )}
      <Tooltip title="How to interact">
        <IconButton
          onClick={(e: React.MouseEvent<HTMLElement>) => setHelpAnchor(e.currentTarget)}
          aria-label="How to interact with the graph"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: "accentInfo30.main",
            "&:hover": { bgcolor: "accentInfo10.main" },
          }}
        >
          <InfoIcon size={28} />
        </IconButton>
      </Tooltip>
      <Popover
        open={helpAnchor !== null}
        anchorEl={helpAnchor}
        onClose={() => setHelpAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ p: 2, maxWidth: 360 }}>
          <Typography variant="heading.seven" sx={{ mb: 1 }}>
            How to interact
          </Typography>
          <List dense disablePadding>
            {[
              "Left-click on empty space in the graph to drag the entire graph.",
              "Left-click and drag any node to move it to a new location.",
              "Left-click a node to view its information and available options.",
            ].map((text) => (
              <ListItem key={text} sx={{ display: "list-item", listStyleType: "disc", pl: 0, ml: 2 }}>
                <Typography variant="body.small.regular">{text}</Typography>
              </ListItem>
            ))}
          </List>
        </Box>
      </Popover>
    </Box>
  );
};
